import { ColumnDef } from '@tanstack/react-table';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { DayTarget } from '../../hooks/use-under-time';
import { TimeSheetRecord, TimeSheetTask } from '../../types/timesheet.types';
import { DayTargetChip } from '../timesheet-week-view/timesheet-week-view';

const formatHours = (hours: number) => `${hours.toFixed(2)}h`;

const totalBillable = (record: TimeSheetRecord) =>
  record.Tasks.reduce((sum, task) => sum + task.Billable, 0);
const totalNonBillable = (record: TimeSheetRecord) =>
  record.Tasks.reduce((sum, task) => sum + task.NonBillable, 0);

/** The list's totals row (keyed by column id) over `records`. */
export const timesheetTotals = (records: readonly TimeSheetRecord[]) => ({
  TaskCount: records.reduce((sum, record) => sum + record.Tasks.length, 0),
  TotalBillable: formatHours(records.reduce((sum, record) => sum + totalBillable(record), 0)),
  TotalNonBillable: formatHours(records.reduce((sum, record) => sum + totalNonBillable(record), 0)),
});

interface CreateTimesheetTableColumnsProps {
  t: (key: string) => string;
  onEdit: (record: TimeSheetRecord) => void;
  onDelete: (record: TimeSheetRecord) => void;
  /** Under time / On target of a logged day (`useDayTarget`). */
  dayTarget: (day: string, tasks: readonly TimeSheetTask[]) => DayTarget | null;
}

export const createTimesheetTableColumns = ({
  t,
  dayTarget,
  onEdit,
  onDelete,
}: CreateTimesheetTableColumnsProps): ColumnDef<TimeSheetRecord>[] => [
  {
    id: 'TaskDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DATE')} />,
    accessorFn: (row) => row.TaskDate,
    cell: ({ row }) => <span>{formatDateOnly(row.original.TaskDate)}</span>,
  },
  {
    id: 'TaskCount',
    // Counted from the tasks JSON after the page loads; not a field the server can sort on.
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('MY_TIMESHEET_TASKS')} />
    ),
    accessorFn: (row) => row.Tasks.length,
  },
  {
    id: 'TotalBillable',
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('MY_TIMESHEET_BILLABLE_HOURS')} />
    ),
    accessorFn: (row) => totalBillable(row),
    cell: ({ row }) => <span>{formatHours(totalBillable(row.original))}</span>,
  },
  {
    id: 'TotalNonBillable',
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('MY_TIMESHEET_NON_BILLABLE_HOURS')} />
    ),
    accessorFn: (row) => totalNonBillable(row),
    cell: ({ row }) => <span>{formatHours(totalNonBillable(row.original))}</span>,
  },
  {
    id: 'DayTarget',
    // Card: the status chip top right. Worked out on the page from the employee's working hours (the
    // stored IsUnderTime is never set), so the server can neither sort nor filter on it.
    enableSorting: false,
    meta: { mobile: 'badge' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => dayTarget(row.TaskDate.slice(0, 10), row.Tasks),
    cell: ({ row }) => (
      <DayTargetChip target={dayTarget(row.original.TaskDate.slice(0, 10), row.original.Tasks)} />
    ),
  },
  {
    id: 'actions',
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(row.original)}
          aria-label={t('EDIT')}
        >
          <PencilSimple className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(row.original)}
          aria-label={t('DELETE')}
        >
          <Trash className="size-4" />
        </Button>
      </div>
    ),
  },
];
